import React, { FC } from 'react';
import {
	formatPounds,
	getChargeReasonLabel,
	getRecoveryReasonLabel,
	getRefrigerantEventTypeLabel,
	getVerificationStageLabel,
	isQuantityBearingEventType,
	REFRIGERANT_EVENT_RESULTS,
} from '../../utils/RefrigerantUtils';
import { nullSafeGet, nullSafeGetWithZero } from '../../utils/DataAccessUtils';

interface RefrigerantEventTypeDisplayProps {
	event: any;
	/** The leak source line under the pill. Off in dense contexts. */
	showLeakSource?: boolean;
	/**
	 * The reason line under the pill — for a charge, why refrigerant went in; for a recovery, why it
	 * came out. Its own flag rather than sharing `showLeakSource`, because the two describe different
	 * events — a Charge never carries a leak source and a repair or inspection never carries a reason —
	 * so one switch would be off for the wrong row.
	 *
	 * ONE flag for both reasons rather than one each: they are mutually exclusive on a row (a charge
	 * has no recovery reason and a recovery has no charge reason), and a caller choosing to show
	 * "why" would never mean only half of it.
	 */
	showChargeReason?: boolean;
	/**
	 * Drops the pill chrome and renders the same text plainly, for the printed servicing record.
	 *
	 * The colour is the carrier of meaning in the pill — red for a failed inspection — and colour is
	 * exactly what does not survive a monochrome print or a photocopy of one. On paper the words have
	 * to stand on their own, so the record shows text and the working ledger shows the pill.
	 */
	plain?: boolean;
}

const NEUTRAL_PILL = { color: 'rgba(0,0,0,0.65)', backgroundColor: '#f2f2f2' };
const PASS_PILL = { color: 'rgb(0, 99, 6)', backgroundColor: '#e1fbe7' };
// A failed inspection or verification is the event that STARTS an obligation, so it is the one thing
// in the ledger that has to be findable by scanning.
const FAIL_PILL = { color: 'rgb(163, 40, 40)', backgroundColor: '#f9e6e7' };

/**
 * One ledger row's event, as the design renders it: `Charge: 52 lb`, `Leak Inspection: Fail`,
 * `Verification Test: Pass (Follow-up)`, `Recover: 41.5 lb`, with the leak source or the reason on a
 * second line when there is one.
 *
 * The suffix differs by type because the types record different things — Charge and Recover carry the
 * regulated quantity, an inspection and a verification test carry an observation, and a repair carries
 * neither. Rendering an empty `: ` on the types that legitimately have no value is what the branching
 * below avoids.
 *
 * A disposal renders as `Recover: 41.5 lb` with `Reason for Recovery: Disposal` underneath, rather than
 * as its own event label, because that is what it now is — see `RECOVERY_REASONS`. The quantity suffix
 * follows `isQuantityBearingEventType` and the pill colour keys off `result` rather than the type, so
 * neither needed a branch for it.
 */
export const RefrigerantEventTypeDisplay: FC<RefrigerantEventTypeDisplayProps> = ({
	event,
	showLeakSource = true,
	showChargeReason = true,
	plain = false,
}) => {
	const eventType = nullSafeGet('eventType', event);
	const result = nullSafeGet('result', event);
	const leakSource = nullSafeGet('leakSource', event);
	const chargeReason = nullSafeGet('chargeReason', event);
	const recoveryReason = nullSafeGet('recoveryReason', event);
	const verificationStage = nullSafeGet('verificationStage', event);

	const pillColors =
		result === REFRIGERANT_EVENT_RESULTS.FAIL
			? FAIL_PILL
			: result === REFRIGERANT_EVENT_RESULTS.PASS
			? PASS_PILL
			: NEUTRAL_PILL;

	const suffix = isQuantityBearingEventType(eventType)
		? formatPounds(nullSafeGetWithZero('amountLb', event))
		: result || null;

	// Through the shared helper rather than an inline mapping. The CSV export reads the same events and
	// has to print the same words — an auditor reads the file against the sheet — so a stage label spelled
	// in two places is a stage label that can disagree with itself.
	const stageLabel = getVerificationStageLabel(verificationStage);
	const stageSuffix = stageLabel ? ` (${stageLabel})` : '';

	return (
		<div className="flex flex-col gap-2xs">
			<span
				className={plain ? 'bodyregular text-character-primary' : 'footnoteregular'}
				style={
					plain
						? undefined
						: {
								display: 'inline-flex',
								whiteSpace: 'nowrap',
								color: pillColors.color,
								background: pillColors.backgroundColor,
								padding: '4px 12px',
								borderRadius: 16,
								alignSelf: 'flex-start',
						  }
				}
			>
				{getRefrigerantEventTypeLabel(eventType)}
				{suffix ? `: ${suffix}` : ''}
				{stageSuffix}
			</span>
			{showLeakSource && leakSource ? (
				<span className="footnoteregular text-character-secondary">Leak Source: {leakSource}</span>
			) : null}
			{/* Only a top-off counts toward the leak rate, so the reason is what explains a charge that
			    is in the ledger and NOT in the regulated figure. A row showing pounds added with no
			    account of why is exactly the row an auditor queries. Absent on legacy rows, which
			    predate the field and do count toward the rate — nothing is asserted for them. */}
			{showChargeReason && chargeReason ? (
				<span className="footnoteregular text-character-secondary">
					Reason for Charge: {getChargeReasonLabel(chargeReason)}
				</span>
			) : null}
			{/* The recovery's counterpart, and the line that carries what a separate Disposal event label
			    used to say. Printed for BOTH reasons, not only for a disposal: a bare `Recover: 41.5 lb`
			    with nothing under it would be indistinguishable from a row whose reason predates the
			    field, and "this charge went back in" is itself the fact an auditor is checking. */}
			{showChargeReason && recoveryReason ? (
				<span className="footnoteregular text-character-secondary">
					Reason for Recovery: {getRecoveryReasonLabel(recoveryReason)}
				</span>
			) : null}
		</div>
	);
};

export default RefrigerantEventTypeDisplay;
